import { useMemo, useState } from 'react'
import { format, isValid, parseISO, subDays } from 'date-fns'
import {
  Activity,
  AlertCircle,
  BarChart3,
  CalendarRange,
  Clock,
  FileChartColumn,
  FileText,
  History,
  TrendingUp,
} from 'lucide-react'
import { Line, LineChart, CartesianGrid, XAxis, YAxis } from 'recharts'

import { useAuth } from '@/features/authentication/components/auth-provider'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Pagination, PaginationContent, PaginationItem, PaginationNext, PaginationPrevious } from '@/components/ui/pagination'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/ui/chart'
import type { ChartConfig } from '@/components/ui/chart'
import { cn } from '@/lib/utils'
import {
  useByMember,
  useGetActivityTrends,
  useGetEngagementScore,
  useGetMemberSummary,
  useGetRecentActivity,
  useGetTotalActivityCount,
} from '@/utils/api/membership-service'
import type {
  ActivitySummaryDTO,
  ApplicationResponseDTO,
  ByMemberParams,
  GetActivityTrendsParams,
  GetEngagementScoreParams,
  GetMemberSummaryParams,
  GetRecentActivityParams,
  ResourceAccessEvent,
} from '@/utils/api/membership-service/models'
import type { DailyActivityDTO } from '@/utils/api/membership-service/models/daily-activity-dt-o'
import type { PageResourceAccessEvent } from '@/utils/api/membership-service/models/page-resource-access-event'

const DAILY_DEFAULT_WINDOW = 30
const DEFAULT_RECENT_WINDOW = 72
const ACTIVITY_PAGE_SIZE = 6

const chartConfig: ChartConfig = {
  count: {
    label: 'Activities',
    color: 'hsl(var(--chart-1))',
  },
}

type MemberActivityInsightsProps = {
  latestApplication: ApplicationResponseDTO | null
  className?: string
}

export function MemberActivityInsights({
  latestApplication,
  className,
}: MemberActivityInsightsProps) {
  const { user } = useAuth()
  const resolvedMemberId =
    user?.id ?? latestApplication?.userId ?? latestApplication?.id ?? null

  const [dateRange, setDateRange] = useState(() => {
    const today = new Date()
    return {
      from: format(subDays(today, DAILY_DEFAULT_WINDOW - 1), 'yyyy-MM-dd'),
      to: format(today, 'yyyy-MM-dd'),
    }
  })
  const [hoursInput, setHoursInput] = useState(String(DEFAULT_RECENT_WINDOW))
  const [pageIndex, setPageIndex] = useState(0)

  const sharedRange = useMemo(() => {
    const params: { from?: string; to?: string } = {}
    if (dateRange.from) params.from = dateRange.from
    if (dateRange.to) params.to = dateRange.to
    return Object.keys(params).length ? params : undefined
  }, [dateRange.from, dateRange.to])

  const summaryParams = sharedRange as GetMemberSummaryParams | undefined
  const engagementParams = sharedRange as GetEngagementScoreParams | undefined
  const trendParams = sharedRange as GetActivityTrendsParams | undefined

  const parsedHours = Number.parseInt(hoursInput, 10)
  const recentWindow = Number.isFinite(parsedHours) && parsedHours > 0 ? parsedHours : DEFAULT_RECENT_WINDOW

  const recentParams = useMemo<GetRecentActivityParams>(() => ({ hours: recentWindow }), [recentWindow])
  const pagedParams = useMemo<ByMemberParams>(
    () => ({
      page: pageIndex,
      size: ACTIVITY_PAGE_SIZE,
    }),
    [pageIndex],
  )

  const summaryQuery = useGetMemberSummary<ActivitySummaryDTO | undefined>(
    resolvedMemberId ?? 0,
    summaryParams,
    {
      query: {
        enabled: Boolean(resolvedMemberId),
        select: (response) => response.data,
      },
    },
  )

  const totalActivityCountQuery = useGetTotalActivityCount<Record<string, unknown> | undefined>(
    resolvedMemberId ?? 0,
    {
      query: {
        enabled: Boolean(resolvedMemberId),
        select: (response) => response.data,
      },
    },
  )

  const engagementQuery = useGetEngagementScore<Record<string, unknown> | undefined>(
    resolvedMemberId ?? 0,
    engagementParams,
    {
      query: {
        enabled: Boolean(resolvedMemberId),
        select: (response) => response.data,
      },
    },
  )

  const trendsQuery = useGetActivityTrends<Record<string, unknown> | undefined>(
    resolvedMemberId ?? 0,
    trendParams,
    {
      query: {
        enabled: Boolean(resolvedMemberId),
        select: (response) => response.data,
      },
    },
  )

  const recentActivityQuery = useGetRecentActivity<ResourceAccessEvent[] | undefined>(
    resolvedMemberId ?? 0,
    recentParams,
    {
      query: {
        enabled: Boolean(resolvedMemberId),
        select: (response) => response.data,
      },
    },
  )

  const activityLogQuery = useByMember<PageResourceAccessEvent | undefined>(
    resolvedMemberId ?? 0,
    pagedParams,
    {
      query: {
        enabled: Boolean(resolvedMemberId),
        placeholderData: (previous) => previous,
        select: (response) => response.data,
      },
    },
  )

  const isAnyLoading =
    summaryQuery.isPending ||
    engagementQuery.isPending ||
    totalActivityCountQuery.isPending ||
    activityLogQuery.isPending ||
    recentActivityQuery.isPending

  const hasAnyError =
    summaryQuery.isError ||
    engagementQuery.isError ||
    totalActivityCountQuery.isError ||
    activityLogQuery.isError ||
    recentActivityQuery.isError

  const summary = summaryQuery.data
  const totalActivityPayload = totalActivityCountQuery.data

  let totalActivities = summary?.totalActivities ?? 0
  if (totalActivityPayload && typeof totalActivityPayload === 'object') {
    for (const value of Object.values(totalActivityPayload)) {
      if (typeof value === 'number') {
        totalActivities = value
        break
      }
    }
  }

  let engagementScore = 0
  let engagementTrend: string | undefined
  const engagementPayload = engagementQuery.data
  if (engagementPayload) {
    if (typeof (engagementPayload as Record<string, unknown>).score === 'number') {
      engagementScore = (engagementPayload as Record<string, unknown>).score as number
    } else {
      const candidates = Object.values(engagementPayload)
      for (const candidate of candidates) {
        if (typeof candidate === 'number') {
          engagementScore = candidate
          break
        }
        if (candidate && typeof candidate === 'object') {
          const record = candidate as Record<string, unknown>
          if (typeof record.score === 'number') {
            engagementScore = record.score
            break
          }
          if (typeof record.value === 'number') {
            engagementScore = record.value
            break
          }
          const nestedNumeric = Object.values(record).find(
            (value) => typeof value === 'number',
          )
          if (typeof nestedNumeric === 'number') {
            engagementScore = nestedNumeric
            break
          }
        }
      }
    }

    if (
      (engagementPayload as Record<string, unknown>).trend &&
      typeof (engagementPayload as Record<string, unknown>).trend === 'string'
    ) {
      engagementTrend = (engagementPayload as Record<string, unknown>).trend as string
    }
  }

  const normalizedEngagementScore = Math.min(
    100,
    Math.max(0, Math.round(engagementScore)),
  )

  const dailySeries = useMemo(() => {
    if (!summary?.dailyBreakdown?.length) return []
    return summary.dailyBreakdown
      .filter(Boolean)
      .map((entry) => {
        const rawDate = (entry as DailyActivityDTO).date ?? ''
        const parsedDate = rawDate ? parseISO(rawDate) : null
        const label =
          parsedDate && isValid(parsedDate) ? format(parsedDate, 'MMM d') : rawDate || 'Unknown'
        return {
          date: label,
          rawDate,
          count: (entry as DailyActivityDTO).count ?? 0,
        }
      })
  }, [summary?.dailyBreakdown])

  const activitiesByType = useMemo(() => {
    if (!summary?.activitiesByType) return []
    return Object.entries(summary.activitiesByType)
      .filter(([, value]) => typeof value === 'number')
      .sort(([, a], [, b]) => (b as number) - (a as number))
  }, [summary?.activitiesByType])

  const activitiesByAction = useMemo(() => {
    if (!summary?.activitiesByAction) return []
    return Object.entries(summary.activitiesByAction)
      .filter(([, value]) => typeof value === 'number')
      .sort(([, a], [, b]) => (b as number) - (a as number))
  }, [summary?.activitiesByAction])

  const recentActivity = (recentActivityQuery.data ?? []).slice(0, 5)

  const activityPage = activityLogQuery.data
  const activityRows = Array.isArray(activityPage?.content)
    ? (activityPage.content.filter(Boolean) as ResourceAccessEvent[])
    : []
  const totalPages =
    typeof activityPage?.totalPages === 'number' && activityPage.totalPages > 0
      ? activityPage.totalPages
      : 1
  const totalElements =
    typeof activityPage?.totalElements === 'number'
      ? activityPage.totalElements
      : activityRows.length

  const trendHighlights = useMemo(() => {
    const payload = trendsQuery.data
    if (!payload || typeof payload !== 'object') return []
    const entries: Array<{ key: string; label: string }> = []
    Object.entries(payload).forEach(([key, value]) => {
      if (!value) return
      if (typeof value === 'number') {
        entries.push({
          key,
          label: `${numberFormatter(value)} events`,
        })
        return
      }
      if (typeof value === 'object') {
        const numericEntry = Object.entries(value as Record<string, unknown>)
          .filter(([, item]) => typeof item === 'number')
          .sort(([, a], [, b]) => (b as number) - (a as number))[0]

        if (numericEntry) {
          entries.push({
            key,
            label: `${numericEntry[0]} · ${numberFormatter(numericEntry[1] as number)} events`,
          })
        }
      }
    })
    return entries.slice(0, 4)
  }, [trendsQuery.data])

  const mostActiveDay = summary?.mostActiveDay
    ? formatDisplayDate(summary.mostActiveDay)
    : null

  const mostAccessedResourceType = summary?.mostAccessedResourceType ?? null

  const resetFilters = () => {
    const today = new Date()
    setDateRange({
      from: format(subDays(today, DAILY_DEFAULT_WINDOW - 1), 'yyyy-MM-dd'),
      to: format(today, 'yyyy-MM-dd'),
    })
    setHoursInput(String(DEFAULT_RECENT_WINDOW))
  }

  const updateRange = (key: 'from' | 'to', value: string) => {
    setDateRange((previous) => {
      const next = { ...previous, [key]: value }
      if (next.from && next.to && next.from > next.to) {
        if (key === 'from') {
          next.to = next.from
        } else {
          next.from = next.to
        }
      }
      return next
    })
  }

  return (
    <section className={cn('space-y-6 rounded-xl border border-dashed border-primary/15 bg-card p-6', className)}>
      <div className="space-y-2">
        <h2 className="text-2xl font-semibold tracking-tight">Activity insights</h2>
        <p className="text-sm text-muted-foreground">
          Track how you interact with institute resources and content over the selected window.
        </p>
      </div>

      {!resolvedMemberId && (
        <Alert variant="default" className="border-amber-200 bg-amber-500/10 text-amber-900 dark:text-amber-100">
          <AlertCircle className="size-4" aria-hidden />
          <AlertTitle>Profile not linked</AlertTitle>
          <AlertDescription>
            We could not determine your member identifier. Submit at least one membership application to unlock usage analytics.
          </AlertDescription>
        </Alert>
      )}

      {resolvedMemberId && (
        <>
          <Card className="border-muted">
            <CardHeader className="flex flex-col gap-2 pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <CalendarRange className="size-4 text-primary" aria-hidden />
                Date & activity filters
              </CardTitle>
              <CardDescription>
                Adjust the analysis window and how far back to look for recent events.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-4">
              <div className="flex flex-col gap-2">
                <label htmlFor="activity-from" className="text-sm font-medium">
                  From
                </label>
                <Input
                  id="activity-from"
                  type="date"
                  value={dateRange.from}
                  max={dateRange.to || undefined}
                  onChange={(event) => updateRange('from', event.target.value)}
                />
              </div>
              <div className="flex flex-col gap-2">
                <label htmlFor="activity-to" className="text-sm font-medium">
                  To
                </label>
                <Input
                  id="activity-to"
                  type="date"
                  value={dateRange.to}
                  min={dateRange.from || undefined}
                  max={format(new Date(), 'yyyy-MM-dd')}
                  onChange={(event) => updateRange('to', event.target.value)}
                />
              </div>
              <div className="flex flex-col gap-2">
                <label htmlFor="recent-window" className="text-sm font-medium">
                  Recent activity window (hours)
                </label>
                <Input
                  id="recent-window"
                  type="number"
                  min={1}
                  value={hoursInput}
                  onChange={(event) => setHoursInput(event.target.value)}
                />
              </div>
              <div className="flex flex-col gap-2 md:items-end md:justify-end">
                <Button type="button" variant="outline" className="w-full md:w-auto" onClick={resetFilters}>
                  Reset filters
                </Button>
              </div>
            </CardContent>
          </Card>

          <div className="grid gap-4 md:grid-cols-3">
            <MetricTile
              tone="primary"
              icon={Activity}
              label="Total tracked activities"
              helper={mostActiveDay ? `Most active on ${mostActiveDay}` : 'Based on analytics API'}
              value={numberFormatter(totalActivities)}
              loading={summaryQuery.isPending && !summary}
            />
            <MetricTile
              tone="emerald"
              icon={FileText}
              label="Unique resources accessed"
              helper={mostAccessedResourceType ? `Frequent type: ${mostAccessedResourceType}` : 'Documents, videos, and pages'}
              value={numberFormatter(summary?.uniqueResources ?? 0)}
              loading={summaryQuery.isPending && !summary}
            />
            <MetricTile
              tone="violet"
              icon={TrendingUp}
              label="Engagement score"
              helper={engagementTrend ?? 'Composite score across your activity mix'}
              value={`${normalizedEngagementScore}%`}
              loading={engagementQuery.isPending && !engagementPayload}
            />
          </div>

          {hasAnyError && (
            <Alert variant="destructive">
              <AlertTitle>Unable to load activity insights</AlertTitle>
              <AlertDescription>
                {summaryQuery.error?.message ||
                  engagementQuery.error?.message ||
                  activityLogQuery.error?.message ||
                  recentActivityQuery.error?.message ||
                  'Please try again later.'}
              </AlertDescription>
            </Alert>
          )}

          <Card className="border-muted">
            <CardHeader className="pb-0">
              <CardTitle className="text-base flex items-center gap-2">
                <BarChart3 className="size-4 text-primary" aria-hidden />
                Daily activity trend
              </CardTitle>
              <CardDescription>Activity volume for each day in the selected range.</CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              {isAnyLoading && dailySeries.length === 0 && (
                <Skeleton className="h-[200px] w-full rounded-lg" />
              )}
              {!isAnyLoading && dailySeries.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  No resource activity was recorded for this period.
                </p>
              )}
              {dailySeries.length > 0 && (
                <ChartContainer config={chartConfig} className="h-[240px] w-full">
                  <LineChart data={dailySeries} margin={{ top: 8, right: 24, left: 8, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="date" tickLine={false} axisLine={false} />
                    <YAxis allowDecimals={false} tickLine={false} axisLine={false} />
                    <ChartTooltip
                      cursor={false}
                      content={
                        <ChartTooltipContent
                          formatter={(value, _name, payload) => (
                            <div className="flex flex-col">
                              <span className="text-muted-foreground text-xs">
                                {payload?.payload?.rawDate
                                  ? formatDisplayDate(payload.payload.rawDate)
                                  : 'Unknown date'}
                              </span>
                              <span className="font-semibold">
                                {numberFormatter(typeof value === 'number' ? value : Number(value))} activities
                              </span>
                            </div>
                          )}
                        />
                      }
                    />
                    <Line
                      type="monotone"
                      strokeWidth={2}
                      dataKey="count"
                      stroke="var(--color-chart-1)"
                      dot={false}
                      activeDot={{ r: 4 }}
                    />
                  </LineChart>
                </ChartContainer>
              )}
            </CardContent>
          </Card>

          <div className="grid gap-4 lg:grid-cols-[2fr_1fr]">
            <Card className="border-muted">
              <CardHeader className="pb-0">
                <CardTitle className="text-base flex items-center gap-2">
                  <History className="size-4 text-primary" aria-hidden />
                  Activity log
                </CardTitle>
                <CardDescription>Recent resource events with pagination.</CardDescription>
              </CardHeader>
              <CardContent className="pt-4">
                {activityRows.length === 0 && activityLogQuery.isPending && (
                  <Skeleton className="h-[160px] w-full rounded-lg" />
                )}
                {activityRows.length === 0 && !activityLogQuery.isPending && (
                  <p className="text-sm text-muted-foreground">No events captured yet.</p>
                )}
                {activityRows.length > 0 && (
                  <>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Resource</TableHead>
                          <TableHead>Action</TableHead>
                          <TableHead>Channel</TableHead>
                          <TableHead>Date</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {activityRows.map((row) => (
                          <TableRow key={`${row.id}-${row.accessedAt}`}>
                            <TableCell>{row.resourceType ?? 'Resource'}</TableCell>
                            <TableCell className="capitalize">{row.action?.toLowerCase() ?? '—'}</TableCell>
                            <TableCell>{row.channel ?? 'Portal'}</TableCell>
                            <TableCell className="text-sm text-muted-foreground">
                              {formatDisplayDateTime(row.accessedAt)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    <div className="mt-4 flex flex-col gap-2 text-xs text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
                      <span>
                        Showing page {pageIndex + 1} of {totalPages} · {numberFormatter(totalElements)} events
                      </span>
                      {totalPages > 1 && (
                        <Pagination>
                          <PaginationContent>
                            <PaginationItem>
                              <PaginationPrevious
                                href="#"
                                onClick={(event) => {
                                  event.preventDefault()
                                  if (pageIndex > 0) {
                                    setPageIndex(pageIndex - 1)
                                  }
                                }}
                                className={cn(pageIndex === 0 && 'pointer-events-none opacity-50')}
                              />
                            </PaginationItem>
                            <PaginationItem>
                              <PaginationNext
                                href="#"
                                onClick={(event) => {
                                  event.preventDefault()
                                  if (pageIndex + 1 < totalPages) {
                                    setPageIndex(pageIndex + 1)
                                  }
                                }}
                                className={cn(pageIndex + 1 >= totalPages && 'pointer-events-none opacity-50')}
                              />
                            </PaginationItem>
                          </PaginationContent>
                        </Pagination>
                      )}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            <div className="space-y-4">
              <Card className="border-muted">
                <CardHeader className="pb-0">
                  <CardTitle className="text-base flex items-center gap-2">
                    <FileChartColumn className="size-4 text-primary" aria-hidden />
                    Activity mix
                  </CardTitle>
                  <CardDescription>Breakdown by resource type and action.</CardDescription>
                </CardHeader>
                <CardContent className="pt-4 space-y-4">
                  {activitiesByType.length === 0 && activitiesByAction.length === 0 && (
                    <p className="text-sm text-muted-foreground">We need more activity to display this breakdown.</p>
                  )}
                  {activitiesByType.length > 0 && (
                    <div>
                      <p className="text-xs uppercase text-muted-foreground mb-2">By resource type</p>
                      <div className="space-y-2">
                        {activitiesByType.slice(0, 4).map(([type, value]) => (
                          <div key={type} className="flex items-center justify-between gap-4 rounded-md border border-dashed border-muted p-2 text-sm">
                            <span>{type}</span>
                            <span className="font-semibold">{numberFormatter(value as number)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {activitiesByAction.length > 0 && (
                    <div>
                      <p className="text-xs uppercase text-muted-foreground mb-2">By action</p>
                      <div className="space-y-2">
                        {activitiesByAction.slice(0, 4).map(([action, value]) => (
                          <div key={action} className="flex items-center justify-between gap-4 rounded-md border border-dashed border-muted p-2 text-sm">
                            <span className="capitalize">{action.toLowerCase()}</span>
                            <span className="font-semibold">{numberFormatter(value as number)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card className="border-muted">
                <CardHeader className="pb-0">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Clock className="size-4 text-primary" aria-hidden />
                    Trend highlights
                  </CardTitle>
                  <CardDescription>Signals derived from the activity trends endpoint.</CardDescription>
                </CardHeader>
                <CardContent className="pt-4 space-y-3">
                  {trendsQuery.isPending && <Skeleton className="h-[120px] w-full rounded-lg" />}
                  {!trendsQuery.isPending && trendHighlights.length === 0 && (
                    <p className="text-sm text-muted-foreground">No highlights available for this window.</p>
                  )}
                  {trendHighlights.length > 0 &&
                    trendHighlights.map((entry) => (
                      <div key={entry.key} className="rounded-md border border-dashed border-muted p-3 text-sm">
                        <p className="font-medium capitalize">{entry.key.replace(/([A-Z])/g, ' $1').toLowerCase()}</p>
                        <p className="text-muted-foreground text-xs">{entry.label}</p>
                      </div>
                    ))}
                </CardContent>
              </Card>
            </div>
          </div>

          <Card className="border-muted">
            <CardHeader className="pb-0">
              <CardTitle className="text-base flex items-center gap-2">
                <FileText className="size-4 text-primary" aria-hidden />
                Recently accessed resources
              </CardTitle>
              <CardDescription>Last {recentActivity.length} events within the chosen window.</CardDescription>
            </CardHeader>
            <CardContent className="pt-4">
              {recentActivity.length === 0 && recentActivityQuery.isPending && (
                <Skeleton className="h-[140px] w-full rounded-lg" />
              )}
              {recentActivity.length === 0 && !recentActivityQuery.isPending && (
                <p className="text-sm text-muted-foreground">No activity yet.</p>
              )}
              <div className="space-y-3">
                {recentActivity.map((entry) => (
                  <div
                    key={`${entry.id}-${entry.accessedAt}`}
                    className="flex flex-col gap-1 rounded-md border border-dashed border-muted p-3 text-sm"
                  >
                    <div className="flex items-center justify-between gap-4">
                      <span className="font-medium">{entry.resourceType ?? 'Resource'}</span>
                      <Badge variant="outline" className="capitalize">
                        {entry.action?.toLowerCase() ?? 'view'}
                      </Badge>
                    </div>
                    <span className="text-muted-foreground text-xs">
                      {formatDisplayDateTime(entry.accessedAt)} · {entry.channel ?? 'Portal'}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </section>
  )
}

function numberFormatter(value: number): string {
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(value)
}

function formatDisplayDate(value?: string | null): string {
  if (!value) return 'Unknown date'
  try {
    const parsed = parseISO(value)
    if (!isValid(parsed)) return value
    return format(parsed, 'PPP')
  } catch {
    return value
  }
}

function formatDisplayDateTime(value?: string | null): string {
  if (!value) return 'Unknown'
  try {
    const parsed = parseISO(value)
    if (!isValid(parsed)) return value
    return format(parsed, 'PP p')
  } catch {
    return value
  }
}

function MetricTile({
  icon: Icon,
  label,
  helper,
  value,
  loading,
  tone,
}: {
  icon: typeof Activity
  label: string
  helper: string
  value: string
  loading?: boolean
  tone: 'primary' | 'emerald' | 'violet'
}) {
  const toneStyles: Record<typeof tone, { card: string; icon: string }> = {
    primary: { card: 'border-primary/20 bg-primary/5', icon: 'text-primary bg-primary/10' },
    emerald: { card: 'border-emerald-500/20 bg-emerald-500/10', icon: 'text-emerald-600 bg-emerald-500/10' },
    violet: { card: 'border-violet-500/20 bg-violet-500/10', icon: 'text-violet-600 bg-violet-500/10' },
  }
  const styles = toneStyles[tone]

  return (
    <Card className={cn('border transition-colors duration-300', styles.card)}>
      <CardContent className="flex flex-col gap-3 p-5">
        <div className="flex items-center gap-2 text-sm font-medium">
          <span className={cn('flex size-9 items-center justify-center rounded-md', styles.icon)}>
            <Icon className="size-4" aria-hidden />
          </span>
          {label}
        </div>
        {loading ? (
          <Skeleton className="h-8 w-24" />
        ) : (
          <p className="text-2xl font-semibold tracking-tight">{value}</p>
        )}
        <p className="text-xs text-muted-foreground">{helper}</p>
      </CardContent>
    </Card>
  )
}
